import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { ChevronDown, ChevronLeft, SquareSlash } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'
import {
  PopulationFilter,
  WorkflowRun,
  WorkflowTemplate,
  isWorkflowRunTerminal,
} from '@digital-twins/shared-types'

import {
  ComposerAppearance,
  composerPanelClassName,
  composerTriggerCaptionClassName,
  composerTriggerChevronClassName,
  composerTriggerClassName,
} from '@/components/composer/composerAppearance'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import i18n from '@/i18n'
import { cn } from '@/lib/utils'
import { templateSurface } from '@/lib/workflows/templateSurface'
import { ChatImage, ThreadAssetView, workflowRunsApi, workflowsApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { workflowRunSegmentFields } from '@/pages/WorkflowsPage/workflowRunSegmentFields'
import { CompareSegmentsGroup } from '@/components/quick-study-results/CompareSegmentsPicker'
import { SubsegmentChip } from '@/components/twin-chat'
import { useAssistantPlatformEnabled } from '@/hooks/useAssistantPlatformEnabled'
import { useWorkflowScheduleAudiences } from '@/pages/WorkflowsPage/useWorkflowScheduleAudiences'

import {
  ThreadCommandInputs,
  composerOwnedTextInputKey,
  hasEveryRequiredInput,
  hideFirstOptionalTextOnCard,
} from './ThreadCommandInputs'
import { TWIN_TEST_SEGMENT_BY_INPUT_KEY } from './TwinTestCutPicker/twinTestCutMapping'
import {
  hasBoundStimulusValue,
  MAX_STIMULUS_INPUT_REFS,
  toStimulusAssetMediaRef,
} from './ThreadStimulusInput'
import { audienceNameForInputs } from './audienceNameForInputs'
import { RememberedAudience } from './useArmedWorkflowState'
import { matchAudienceIdFromHint } from './matchAudienceIdFromHint'
import {
  THREAD_ACTION_SLUG_PREFIX,
  ThreadCommand,
  useThreadCommands,
} from './useThreadCommands'

/**
 * Stable key for test ids and React keys, from the slug rather than from the
 * command name: `/test` is an alias whose slug is `thread-action-twin-test`,
 * and the ids readers' tests already point at spell the slug's half.
 */
function commandKey(slug: string): string {
  return slug.startsWith(THREAD_ACTION_SLUG_PREFIX)
    ? slug.slice(THREAD_ACTION_SLUG_PREFIX.length)
    : slug
}

/**
 * Seeds the panel straight onto a command's confirm screen — typed `/slack`,
 * or a plan workflow Start (thread-action **or** lobby slug such as
 * `morning-digest`). Plan Starts MUST use this seed path, never
 * confirm-less `WorkflowMenu`. A NATIVE command (`/report`) has no confirm
 * screen: the same seed arms it through `onArmReport` and the panel stays
 * shut.
 *
 * Provide `command` (typed path) and/or `slug` (plan Start). At least one is
 * required; when both are set, `slug` wins so a plan row cannot collide with a
 * curated alias (`/test` ↔ `thread-action-twin-test`).
 *
 * `nonce` exists because the same Start / typed command twice must reopen the
 * panel twice; a value-equal seed would otherwise look like no change at all.
 * The page bumps it on every resolve, and this menu applies each nonce once.
 */
export interface ThreadCommandSeed {
  /** The name as typed, including the slash: `/report`. */
  command?: string
  /**
   * Registry / template slug. Resolves thread-actions via `useThreadCommands`
   * and lobby templates via `GET /workflows/by-slug/:slug`.
   */
  slug?: string
  /** Whatever followed the command name. Prefills the first `text` input. */
  rest: string
  nonce: number
  /**
   * When true (plan / NL seeds), never prefill the confirm card's audience
   * field from a remembered audience. The confirm screen always opens and
   * always requires an explicit click regardless of this flag now — its only
   * remaining job is suppressing the remembered-audience prefill so a plan /
   * NL seed's own audience (or hint) is not silently overridden. Typed
   * `/test` omits this, so a remembered audience still pre-fills the picker
   * as a convenience default.
   */
  forceConfirm?: boolean
  /**
   * Optional population token from a plan step's `inputs.audience`. Prefills
   * the confirm audience field when the token is still in the ready list.
   * Plan seeds still set `forceConfirm`, which only affects the
   * remembered-audience prefill now.
   */
  audience?: string
  /**
   * Prose that may name a ready audience (assistant plan text). Used only
   * when `audience` is missing; never applies the remembered audience.
   */
  audienceHint?: string
  /**
   * Structured segment from `commandSeed`. Confirm copies these onto
   * start-run as sibling DTO keys — never into `rest`.
   */
  populationFilter?: PopulationFilter
  segmentLabel?: string
}

function seedMatchesPendingCommand(
  seed: ThreadCommandSeed | null | undefined,
  command: ThreadCommand,
): boolean {
  if (!seed) {
    return false
  }
  if (seed.slug) {
    return seed.slug === command.slug
  }
  if (seed.command) {
    return seed.command === command.command
  }
  return false
}

function seedSegmentLabelForCommand(
  seed: ThreadCommandSeed | null | undefined,
  command: ThreadCommand,
): string | null {
  if (!seedMatchesPendingCommand(seed, command)) {
    return null
  }
  const label =
    typeof seed?.segmentLabel === 'string' ? seed.segmentLabel.trim() : ''
  return label.length > 0 ? label : null
}

/**
 * The thread's COMMAND MENU: one door in the control row, listing everything a
 * reader can hand to a workflow without leaving the thread.
 *
 * A menu rather than the row of chips this started as. The chips grew with the
 * catalogue and pushed the control row onto a second line; a single door that
 * SCROLLS holds as many commands as the org seeds and costs one button's width
 * whatever that number is. Each row names the command the reader could have
 * typed (`/report`), so the menu and the typed form stay the same vocabulary —
 * and both now read the same list, `useThreadCommands`, which derives it from
 * the seeded `thread-action-*` templates instead of a table in this file.
 *
 * TWO SCREENS, ONE PANEL. Picking a row does not start anything: it turns the
 * panel into a confirm step naming what the command will do and collecting the
 * inputs its template declares, and only the button in that step starts a run.
 * Nothing here is exempt from that gate — `/slack` posts outside the product,
 * `/test` spends real run budget, and a one-click list sitting
 * under a textarea is exactly where a misfire happens. A NATIVE command
 * (`/report`) is the one row that opens no confirm screen, because it starts
 * no run: it arms a scoping conversation, which is itself the gate. The confirm step lives
 * INSIDE the same panel rather than in a popover of its own, so there is never
 * a menu and a dialog fighting over the same click.
 *
 * The confirm step's fields come off the template (`GET /workflows/:token`),
 * never off a picker keyed to a slug this file recognises: today that is one
 * audience input on `/test`, tomorrow it is whatever a template declares, with
 * no edit here.
 *
 * Confirmed, a command starts an ordinary run through the ordinary
 * `POST /workflow-runs`, tagged `trigger: 'thread_action'`, and hands the
 * created run straight back to the page so the run pane fills and the sidebar
 * dot lights without a reload. `onRunStarted` fires ONLY for a run the server
 * really created: a refusal (notably the one-active-run-per-thread 409) toasts
 * and adopts nothing, so a thread never shows a run that does not exist.
 *
 * Owns its own feature gate rather than being gated at the mount site like the
 * control row's other buttons: the whole menu is one unit, and the page should
 * mount it in one line.
 *
 * Click-outside-to-close is copied from `WorkflowMenu`, its neighbour in the
 * same row, rather than the design-system `DropdownMenu`, so both doors in the
 * row open and close the same way.
 */
export interface ThreadCommandMenuProps {
  /**
   * Thread the runs report into. `null` on Home Lab / lobby (no thread yet)
   * or while the thread record is still loading. The menu still lists and
   * confirms when the assistant platform is on; start-now then uses
   * `ensureThread` to mint one before `workflowRunsApi.create`.
   */
  threadToken: string | null
  /**
   * Mint a thread when start-now is confirmed and `threadToken` is null.
   * Returns the new thread token. Omitted, start-now still bails without a
   * token — `/test` arming never needs this.
   */
  ensureThread?: () => Promise<string>
  /**
   * Where the panel opens relative to the trigger. Default `above` keeps the
   * thread under-row opening upward. Home Lab and the lobby pass `below`.
   */
  placement?: 'above' | 'below'
  /** Hide the toolbar entry while keeping typed-command confirmation mounted. */
  showTrigger?: boolean
  /** Render the existing setup confirmation in the conversation. */
  confirmationTarget?: HTMLElement | null
  /** Keyboard/Back dismissal; outside clicks retain their own focus target. */
  onDismiss?: () => void
  /**
   * The thread's current run (`useWorkflowRun().run`). A non-terminal one
   * disables the menu: the server allows one active run per thread and would
   * answer a second start with a 409.
   */
  activeRun: WorkflowRun | null
  /** `useWorkflowRun().adoptRun`. Called only for a run that really started. */
  onRunStarted: (run: WorkflowRun) => void
  /**
   * Confirming a command whose `ThreadCommand.arms` is true (`/test`, and
   * `/report`, which arms straight off the pick with no confirm click in
   * between) calls this instead of `workflowRunsApi.create`: the resolved
   * template plus the confirm screen's own answers, keyed the same way
   * `startCommand`'s `inputs` argument is (`{ audience: selectedAudienceId
   * }`). The mount site is expected to feed the template's slug into the same
   * `armedWorkflowSlug` state. The Workflows door only looks selected for a
   * lobby slug; pass `isSelected` here when the armed slug is a thread-action
   * so `/test` paints Commands, not Workflows. This menu does not do that
   * wiring itself: it only reports the decision and its inputs.
   */
  onArm: (
    template: WorkflowTemplate,
    inputs: Record<string, string | null>,
    armDetails?: { audienceName: string | null },
  ) => void
  /**
   * Picking a NATIVE command (`ThreadCommand.kind === 'report'`) calls this
   * instead of `onArm` / `workflowRunsApi.create`: there is no template to
   * resolve, no input to collect and no run to start, so the panel arms the
   * mount site's scoping intake on the pick and closes.
   *
   * `rest` is whatever followed the typed name (`/report on price only`) and
   * is empty for a pick from the list. The mount site decides what arming
   * MEANS, exactly as it does for `onArm`. Optional: an off-thread composer is
   * narrowed to `/test` and never sees a native command.
   */
  onArmReport?: (command: ThreadCommand, rest: string) => void
  /**
   * Picking a skill TAG (`ThreadCommand.kind === 'skill'`, `/<skill-slug>`)
   * calls this instead of `onArm` / `workflowRunsApi.create`: a tag attaches
   * that one skill to the next ordinary assistant turn, with no template, no
   * input and no run. `rest` is whatever followed the typed name and is empty
   * for a pick from the list. Optional: an off-thread composer lists no tags.
   */
  onArmSkill?: (command: ThreadCommand, rest: string) => void
  /**
   * Remembered audience, scoped to the current organization. Pre-fills the
   * confirm card's audience field as a convenience default when it is still
   * a valid choice in the ready list (`applyRememberedAudience` gates on
   * that): it never arms a command and never skips the confirm panel, which
   * always renders and always requires an explicit click before a run starts.
   */
  rememberedAudience?: RememberedAudience | null
  /**
   * Persist the confirm-screen audience. Called only from confirm, and only
   * when the card's remember checkbox is on — never on toggle.
   */
  onRememberAudience?: (audience: RememberedAudience) => void
  /**
   * Forgets the org's remembered audience. Called only from confirm, and only
   * when the checkbox opened checked (the prefilled audience matched the
   * remembered one) and the reader unticked it before confirming — the only
   * forget path (plan 2026-09-14-twin-test-bound-audience, D6). Ticking a
   * DIFFERENT audience still goes through `onRememberAudience`, which
   * overwrites the stored value.
   */
  onForgetRememberedAudience?: () => void
  /**
   * Start the audience list fetch on the arm path. The menu already latches
   * this away from page load; the page ORs this in so an already-armed
   * command does not refetch the list on remount.
   */
  prelatchAudiences?: boolean
  appearance?: ComposerAppearance
  /**
   * Armed thread-action (`/test`). Lights this trigger the same way
   * `WorkflowMenu` lights for a lobby pick. Default off.
   */
  isSelected?: boolean
  className?: string
  /**
   * A command resolved from something the reader typed in the composer. Opens
   * the panel on that command's confirm screen: the typed path and the picked
   * path converge here, on one approval gate and one `create` call site.
   */
  seed?: ThreadCommandSeed | null
  /**
   * Thread-level registry. `applySeedStimulus` binds every hostable registry
   * ref (upload order, oldest first), then composer tiles not already bound,
   * capped at `MAX_STIMULUS_INPUT_REFS`.
   */
  threadAssets?: Array<ThreadAssetView>
  /**
   * Hosted composer tiles (`threadAttachments`). After registry refs, tiles
   * not already bound by `fileId` are appended. Still-uploading tiles
   * (`!url` or `!fileId`) are skipped.
   */
  composerAttachments?: Array<ChatImage>
  /** Demographic cut groups for the optional `segment_by` twin-test input. */
  cutGroups?: Array<CompareSegmentsGroup>
  /** Prefill `segment_by` from the findings overlay picker. */
  segmentByPrefill?: string | null
}

/**
 * Prefill from a remembered audience, gated on it still being a valid pick
 * in the live ready list — mirrors `applySeedAudience`'s own gate. Without
 * this, a stale or deleted remembered id would pre-fill the field while
 * `AudienceSelector` shows its empty placeholder, and `hasEveryRequiredInput`
 * would still count the field as satisfied with nothing visible to confirm.
 */
function applyRememberedAudience(
  values: Record<string, string | null>,
  template: WorkflowTemplate,
  rememberedAudienceId: string | null | undefined,
  audiences: Array<{ id: string; name: string }>,
): Record<string, string | null> {
  if (!rememberedAudienceId) {
    return values
  }
  if (!audiences.some((entry) => entry.id === rememberedAudienceId)) {
    return values
  }

  const next = { ...values }
  for (const spec of template.inputs) {
    if (spec.type === 'audience' && !(next[spec.key] ?? '').trim()) {
      next[spec.key] = rememberedAudienceId
    }
  }
  return next
}

/**
 * Prefill audience from a plan-seeded token when it is still in the ready list,
 * else from a unique audience name in the plan prose hint.
 * Applied before remembered-audience fill so a blank field can take either;
 * plan token/hint wins when both would apply because this runs first and
 * `applyRememberedAudience` only fills blanks. Plan `forceConfirm` seeds skip
 * remembered fill entirely.
 */
function applySeedAudience(
  values: Record<string, string | null>,
  template: WorkflowTemplate,
  audienceToken: string | null | undefined,
  audiences: Array<{ id: string; name: string }>,
  audienceHint = '',
): Record<string, string | null> {
  let resolved =
    audienceToken && audiences.some((entry) => entry.id === audienceToken)
      ? audienceToken
      : ''
  if (!resolved) {
    resolved = matchAudienceIdFromHint(audiences, audienceHint)
  }
  if (!resolved) {
    return values
  }

  const next = { ...values }
  for (const spec of template.inputs) {
    if (spec.type === 'audience' && !(next[spec.key] ?? '').trim()) {
      next[spec.key] = resolved
    }
  }
  return next
}

/**
 * Hostable refs in upload order: registry (oldest `createdAt` first), then
 * composer tiles not already in the set. Cap `MAX_STIMULUS_INPUT_REFS`.
 */
function hostedStimulusRefs(
  threadAssets: Array<ThreadAssetView> | undefined,
  composerAttachments: Array<ChatImage> | undefined,
) {
  const seen = new Set<string>()
  const refs: Array<NonNullable<ReturnType<typeof toStimulusAssetMediaRef>>> =
    []

  function push(mapped: ReturnType<typeof toStimulusAssetMediaRef>): void {
    if (!mapped) {
      return
    }
    const fileId = mapped.fileId?.trim()
    if (!fileId || seen.has(fileId)) {
      return
    }
    seen.add(fileId)
    refs.push(mapped)
  }

  const assets = [...(threadAssets ?? [])].sort((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  )
  for (const asset of assets) {
    push(toStimulusAssetMediaRef(asset.ref))
  }
  for (const attachment of composerAttachments ?? []) {
    push(toStimulusAssetMediaRef(attachment))
  }
  return refs.slice(0, MAX_STIMULUS_INPUT_REFS)
}

/**
 * Prefill blank `type: 'stimulus'` inputs from every hostable registry
 * asset, then hosted composer tiles, capped at `MAX_STIMULUS_INPUT_REFS`.
 * Still-hosting tiles are skipped. An already-bound JSON value is left alone.
 */
export function applySeedStimulus(
  values: Record<string, string | null>,
  template: WorkflowTemplate,
  threadAssets: Array<ThreadAssetView> | undefined,
  composerAttachments: Array<ChatImage> | undefined,
): Record<string, string | null> {
  const refs = hostedStimulusRefs(threadAssets, composerAttachments)
  if (refs.length === 0) {
    return values
  }

  const serialized = JSON.stringify(refs)

  let didPatch = false
  const next = { ...values }
  for (const spec of template.inputs) {
    if (spec.type === 'stimulus' && !hasBoundStimulusValue(next[spec.key])) {
      next[spec.key] = serialized
      didPatch = true
    }
  }
  return didPatch ? next : values
}

export function ThreadCommandMenu(props: ThreadCommandMenuProps) {
  const {
    threadToken,
    ensureThread,
    placement = 'above',
    showTrigger = true,
    confirmationTarget = null,
    onDismiss,
    activeRun,
    onRunStarted,
    onArm,
    onArmReport,
    onArmSkill,
    rememberedAudience = null,
    onRememberAudience,
    onForgetRememberedAudience,
    prelatchAudiences = false,
    appearance = 'external',
    isSelected = false,
    className,
    seed,
    threadAssets,
    composerAttachments,
    cutGroups = [],
    segmentByPrefill = null,
  } = props

  const { t } = useTranslation('app')

  const [startingSlug, setStartingSlug] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  /** The command whose confirm step is showing, if the panel is on that screen. */
  const [pendingSlug, setPendingSlug] = useState<string | null>(null)
  /**
   * Latches on the first template that declares an audience input. The audience
   * list is a 500-row fetch and most thread visits never reach that screen, so
   * the page should not pay for it on load; latching (rather than tracking the
   * open state directly) keeps a close-and-reopen from refetching.
   */
  const [audiencesRequested, setAudiencesRequested] = useState(false)
  /** The confirm screen's answers, keyed by the template's own input keys. */
  const [inputValues, setInputValues] = useState<Record<string, string | null>>(
    {},
  )
  /**
   * Card checkbox. Default off. This menu does not write to storage on
   * toggle — `onRememberAudience` fires only from confirm.
   */
  const [rememberAudience, setRememberAudience] = useState(false)
  /**
   * Lobby (or otherwise non-thread-action) command resolved from a plan Start
   * seed. Thread-action seeds stay on `commands`; this holds the synthetic
   * `ThreadCommand` for `morning-digest` and friends so confirm works
   * without putting lobby rows in the Commands list.
   */
  const [seededExtraCommand, setSeededExtraCommand] =
    useState<ThreadCommand | null>(null)
  /**
   * Plan / typed seed asked for a slug that `resolveTemplateBySlug` rejected
   * (404 inactive / not-in-org). Shown in the panel so Start never fails
   * silently with the nonce dropped and no confirm.
   */
  const [seedResolveError, setSeedResolveError] = useState<string | null>(null)
  const [isFilterStep, setIsFilterStep] = useState(false)
  /**
   * D4: a prepared (`forceConfirm`) seed whose audience already resolves to a
   * loaded option shows a one-line summary instead of the picker. This is the
   * reader's explicit "actually, let me pick" escape hatch out of that
   * summary, back to the ordinary field. Reset wherever a fresh card opens
   * (a new pick, a new seed, reopening the panel, or backing out of confirm)
   * so a later prepared card does not inherit an earlier reveal.
   */
  const [isAudiencePickerRevealed, setIsAudiencePickerRevealed] =
    useState(false)

  const rootRef = useRef<HTMLDivElement>(null)
  const confirmationRef = useRef<HTMLDivElement>(null)
  const onArmReportRef = useRef(onArmReport)
  onArmReportRef.current = onArmReport
  const onArmSkillRef = useRef(onArmSkill)
  onArmSkillRef.current = onArmSkill
  /**
   * A typed command's trailing text, held until the template resolves and says
   * which input (if any) it belongs in. A ref rather than state because
   * nothing renders it: it is consumed exactly once, by the effect that seeds
   * the values.
   */
  const seedRestRef = useRef<string | null>(null)
  /**
   * Survives the template-seed effect re-running when the audience list lands.
   * `seedRestRef` is consumed on the first pass; without this latch, a seeded
   * `/test <ask>` (or plan NL rest) would wipe back to null on that refresh.
   */
  const appliedAskRestRef = useRef<string | null>(null)
  /**
   * Plan-step `inputs.audience` token. Latched like ask rest so an audience-list
   * refresh can apply it once the ready list includes the token.
   */
  const seedAudienceRef = useRef<string | null>(null)
  const seedAudienceHintRef = useRef<string | null>(null)
  const seedIgnoreRememberedRef = useRef(false)
  /**
   * True once the reader has touched the remember-audience checkbox THIS card
   * open. Reset on every fresh pick (`openCommand`, `openMatched`) alongside
   * `rememberAudience` itself. While untouched, the seed effect keeps syncing
   * the checkbox to "prefilled audience === remembered audience" as the
   * audience list and template settle in (D6); once touched, that effect
   * leaves the reader's own choice alone so a later rerender (an attachment
   * landing, say) cannot silently flip a manual uncheck back on.
   */
  const rememberAudienceTouchedRef = useRef(false)
  const appliedAudienceRef = useRef<string | null>(null)
  const appliedAudienceHintRef = useRef<string | null>(null)
  const appliedSeedNonceRef = useRef<number | null>(null)

  const assistantPlatformEnabled = useAssistantPlatformEnabled()
  const isEnabled = assistantPlatformEnabled

  /* D5: off-thread composers omit `ensureThread` and have no token yet.
     In-thread always has a token (or still passes `ensureThread` on the
     mint-then-start path). Infer the surface from that pair so this menu
     does not have to take a `surface` prop of its own. */
  const commandSurface =
    ensureThread == null && threadToken == null ? 'off-thread' : 'thread'
  const { commands, isLoading: commandsLoading } = useThreadCommands(
    isEnabled,
    { surface: commandSurface },
  )
  const pending =
    commands.find((command) => command.slug === pendingSlug) ??
    (seededExtraCommand && seededExtraCommand.slug === pendingSlug
      ? seededExtraCommand
      : null)

  /**
   * The pending command's FULL template, for the inputs it declares. Keyed
   * `['workflow-template', token]` like `useWorkflowTemplate`, so a command
   * opened twice in one session is read once.
   *
   * Fetched only on the confirm screen. The list this menu renders comes from
   * summaries, which carry no `inputs`, and fetching every template up front
   * would be one request per seeded command for a panel most visits never open.
   */
  const pendingToken = pending?.token ?? null
  const pendingSeedSegmentLabel = pending
    ? seedSegmentLabelForCommand(seed, pending)
    : null
  const { data: pendingTemplate, error: templateError } = useSWR(
    pendingToken ? ['workflow-template', pendingToken] : null,
    () => workflowsApi.getTemplate(pendingToken!),
  )

  const { audiences, isLoading: audiencesLoading } =
    useWorkflowScheduleAudiences(audiencesRequested || prelatchAudiences)

  /**
   * D4: whether the confirm card should show a resolved-audience summary line
   * instead of the picker. Reads the card's OWN state (the seed that armed
   * this command, and the audience input's current value against the loaded
   * list) rather than `appliedAudienceRef`, which is written before
   * validation and stays null on the hint-resolved plan path
   * (`ThreadDetailPage.tsx` sets the token from a name match, never that
   * ref). `seedMatchesPendingCommand` keeps this from firing on a seed left
   * over for a different command. While the audience list is still loading,
   * or the seed's token is not (yet, or ever) in it, this stays false and the
   * ordinary picker with its label renders exactly as before this change.
   */
  const pendingAudienceKey = pendingTemplate?.inputs.find(
    (spec) => spec.type === 'audience',
  )?.key
  const resolvedAudienceOption = pendingAudienceKey
    ? audiences.find(
        (entry) => entry.id === (inputValues[pendingAudienceKey] ?? '').trim(),
      )
    : undefined
  const showResolvedAudience =
    pending != null &&
    seedMatchesPendingCommand(seed, pending) &&
    seed?.forceConfirm === true &&
    !audiencesLoading &&
    resolvedAudienceOption != null &&
    !isAudiencePickerRevealed

  /**
   * Seeds one blank answer per declared input the moment the template resolves,
   * so the submitted record is exactly the template's own keys and nothing
   * else, and spends the typed command's trailing text on the first `text`
   * input. A template that declares none discards it (`/slack please hurry`
   * runs `/slack`), which is the plan's stated behaviour rather than an
   * oversight: there is nowhere honest to put it.
   *
   * Ask rest is latched in `appliedAskRestRef` so an audience-list refresh
   * (this effect depends on `audiences`) cannot wipe a seeded `/test <ask>`.
   */
  useEffect(() => {
    if (!pendingTemplate) {
      return
    }
    if (seedRestRef.current) {
      appliedAskRestRef.current = seedRestRef.current
      seedRestRef.current = null
    }
    if (seedAudienceRef.current) {
      appliedAudienceRef.current = seedAudienceRef.current
      seedAudienceRef.current = null
    }
    if (seedAudienceHintRef.current) {
      appliedAudienceHintRef.current = seedAudienceHintRef.current
      seedAudienceHintRef.current = null
    }

    const seeded: Record<string, string | null> = {}
    for (const spec of pendingTemplate.inputs) {
      seeded[spec.key] = null
    }

    const rest = appliedAskRestRef.current
    if (rest) {
      const firstText = pendingTemplate.inputs.find(
        (spec) => spec.type === 'text',
      )
      if (firstText) {
        seeded[firstText.key] = rest
      }
    }

    const withPlanAudience = applySeedAudience(
      seeded,
      pendingTemplate,
      appliedAudienceRef.current,
      audiences,
      appliedAudienceHintRef.current ?? '',
    )
    const withRemembered = seedIgnoreRememberedRef.current
      ? withPlanAudience
      : applyRememberedAudience(
          withPlanAudience,
          pendingTemplate,
          rememberedAudience?.audienceId,
          audiences,
        )
    const withStimulus = applySeedStimulus(
      withRemembered,
      pendingTemplate,
      threadAssets,
      composerAttachments,
    )

    /* D6: the checkbox opens CHECKED when the audience this card prefilled is
       the org's remembered one — re-synced on every pass so a late-arriving
       audience list (the fetch now starts on open, C4, but can still resolve
       after the template) still lands correctly. Only while UNTOUCHED this
       open: once the reader flips the box by hand, later reruns (a stimulus
       landing, say) must not silently flip it back. */
    if (!rememberAudienceTouchedRef.current) {
      const audienceSpec = pendingTemplate.inputs.find(
        (spec) => spec.type === 'audience',
      )
      const prefilledAudienceId = audienceSpec
        ? withRemembered[audienceSpec.key]
        : null
      setRememberAudience(
        Boolean(
          rememberedAudience &&
            prefilledAudienceId &&
            prefilledAudienceId === rememberedAudience.audienceId,
        ),
      )
    }

    setInputValues((previous) => {
      const next = { ...withStimulus }
      for (const spec of pendingTemplate.inputs) {
        if ((previous[spec.key] ?? '').trim().length > 0) {
          next[spec.key] = previous[spec.key]
        }
      }
      if (
        segmentByPrefill &&
        pendingTemplate.inputs.some(
          (spec) => spec.key === TWIN_TEST_SEGMENT_BY_INPUT_KEY,
        ) &&
        !(next[TWIN_TEST_SEGMENT_BY_INPUT_KEY] ?? '').trim()
      ) {
        next[TWIN_TEST_SEGMENT_BY_INPUT_KEY] = segmentByPrefill
      }
      return next
    })
  }, [
    pendingTemplate,
    rememberedAudience,
    audiences,
    segmentByPrefill,
    threadAssets,
    composerAttachments,
  ])

  /**
   * Hosted registry rows or composer tiles may land after the card opens.
   * Patch only blank stimulus inputs so that completion cannot reseed
   * audience, text, or an already-bound JSON value.
   */
  useEffect(() => {
    if (!pendingTemplate) {
      return
    }
    setInputValues((previous) =>
      applySeedStimulus(
        previous,
        pendingTemplate,
        threadAssets,
        composerAttachments,
      ),
    )
  }, [pendingTemplate, threadAssets, composerAttachments])

  /**
   * Applies a typed or plan-Start seed. Each nonce is applied at most once, so
   * an SWR revalidation that hands back a fresh `commands` array cannot reopen
   * a panel the reader has closed. A seed that arrives before the thread-action
   * list (or while by-slug is in flight) stays unapplied until that lands. A
   * definitive by-slug failure consumes the nonce and surfaces an error — a
   * later Start bumps the nonce to retry.
   */
  useEffect(() => {
    if (!seed || seed.nonce === appliedSeedNonceRef.current) {
      return
    }
    if (!seed.command && !seed.slug) {
      return
    }

    const seedNonce = seed.nonce
    const seedRest = seed.rest
    const seedSlug = seed.slug
    const seedCommand = seed.command
    const seedForceConfirm = seed.forceConfirm === true
    const seedAudience =
      typeof seed.audience === 'string' ? seed.audience.trim() : ''
    const seedAudienceHint =
      typeof seed.audienceHint === 'string' ? seed.audienceHint.trim() : ''

    function openMatched(match: ThreadCommand) {
      appliedSeedNonceRef.current = seedNonce
      /* A native command has no template, so there is nothing to confirm and
         nothing to collect: the seed's trailing text is the scoping opener,
         not an input value. `forceConfirm` (plan / NL seeds) has no confirm
         screen to force here. */
      if (match.kind === 'report') {
        setSeedResolveError(null)
        setSeededExtraCommand(null)
        setPendingSlug(null)
        setIsOpen(false)
        onArmReportRef.current?.(match, seedRest)
        return
      }
      /* A skill tag is not a run either: the trailing text is the ask the
         tagged turn answers, handed straight to the mount site. */
      if (match.kind === 'skill') {
        setSeedResolveError(null)
        setSeededExtraCommand(null)
        setPendingSlug(null)
        setIsOpen(false)
        onArmSkillRef.current?.(match, seedRest)
        return
      }
      seedRestRef.current = seedRest
      appliedAskRestRef.current = null
      seedAudienceRef.current = seedAudience || null
      appliedAudienceRef.current = null
      seedAudienceHintRef.current = seedAudienceHint || null
      appliedAudienceHintRef.current = null
      /* Plan / NL seeds ignore the remembered-audience prefill; typed `/test`
         does not, so a remembered audience still pre-fills the picker as a
         convenience default. Either way the confirm screen always opens and
         always requires an explicit click before a run starts. */
      seedIgnoreRememberedRef.current = seedForceConfirm
      setSeedResolveError(null)
      setInputValues({})
      setRememberAudience(false)
      rememberAudienceTouchedRef.current = false
      setIsAudiencePickerRevealed(false)
      /* C4: request the ready audience list the moment the card opens, before
         the template (and this seed) resolve — a plan/typed seed cannot yet
         say whether the matched command declares an audience input, so this
         fires unconditionally rather than waiting to find out. The hook
         itself latches on `enabled`, so an extra request for a command that
         turns out to have no audience field costs nothing further. */
      setAudiencesRequested(true)
      setPendingSlug(match.slug)
      setIsOpen(true)
    }

    function surfaceSeedResolveFailure(error: unknown) {
      appliedSeedNonceRef.current = seedNonce
      const message = getApiErrorMessage(
        error,
        i18n.t('threads.detail.toast.workflowUnavailable', { ns: 'app' }),
      )
      setSeedResolveError(message)
      setSeededExtraCommand(null)
      setPendingSlug(null)
      setIsOpen(true)
      toast.error(message)
    }

    const fromCommands = seedSlug
      ? commands.find((command) => command.slug === seedSlug)
      : commands.find((command) => command.command === seedCommand)

    if (fromCommands) {
      setSeededExtraCommand(null)
      openMatched(fromCommands)
      return
    }

    /* Lobby / non-thread-action slug: resolve token server-side, then open the
       same confirm screen. Never arm via WorkflowMenu from a plan. Wait for
       the thread-action list so a late-loading `/test` seed is not treated as
       a lobby by-slug miss. */
    if (!seedSlug) {
      return
    }
    if (commandsLoading) {
      return
    }

    let cancelled = false
    void workflowsApi
      .resolveTemplateBySlug(seedSlug)
      .then((summary) => {
        if (cancelled) {
          return
        }
        const synthetic: ThreadCommand = {
          slug: summary.slug,
          token: summary.token,
          command: `/${summary.slug}`,
          label: summary.displayName,
          blurb: summary.description,
          summary: '',
          confirmLabel: i18n.t('threads.detail.command.startNamed', {
            name: summary.displayName,
            ns: 'app',
          }),
          prompt: summary.description,
          startedMessage: i18n.t('threads.detail.command.startedNamed', {
            name: summary.displayName,
            ns: 'app',
          }),
          arms: false,
          /* Resolved from `GET /workflows/by-slug`, so it is a workflow by
             construction: a native command has no template to resolve. */
          kind: 'workflow',
        }
        setSeededExtraCommand(synthetic)
        openMatched(synthetic)
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        surfaceSeedResolveFailure(error)
      })

    return () => {
      cancelled = true
    }
  }, [seed, commands, commandsLoading])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (rootRef.current?.contains(target) || confirmationRef.current?.contains(target)) {
        return
      }
      /**
       * A click inside a Radix popper is NOT a click outside this menu.
       *
       * The confirm screen renders the template's declared inputs, and both the
       * `audience` picker (Popover) and a `select` input (Select) render their
       * open list through a PORTAL on `document.body`. That list is visually
       * inside the panel and structurally outside it, so the containment test
       * above reads a click on an option as a dismissal and closes the whole
       * panel on mousedown, before the option's own click can land. The symptom
       * is that picking an audience does nothing at all: the panel disappears,
       * the value is never set, and the confirm button never enables.
       *
       * Radix wraps every portalled popper in `[data-radix-popper-content-wrapper]`,
       * which is the one marker common to Popover, Select and Dropdown, so this
       * holds for any input kind a template declares rather than for the
       * audience picker alone. Each of those layers dismisses itself on an
       * outside click, so nothing here needs to close them.
       */
      if (
        target instanceof Element &&
        target.closest('[data-radix-popper-content-wrapper]')
      ) {
        return
      }
      setIsOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        onDismiss?.()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onDismiss])

  const isRunActive = Boolean(
    activeRun && !isWorkflowRunTerminal(activeRun.status),
  )
  const isStarting = startingSlug !== null
  const isDisabled = isRunActive || isStarting
  const disabledReason = isRunActive
    ? t('threads.detail.runAlreadyActive')
    : isStarting
      ? t('threads.detail.starting')
      : undefined
  /**
   * The confirm button's gate. A template that has not resolved — still in
   * flight, or refused — leaves it disabled: an enabled button over zero
   * rendered fields would post a run missing its required input and come back
   * as a server refusal the reader cannot act on.
   */
  const canConfirm =
    !isDisabled &&
    pendingTemplate !== undefined &&
    hasEveryRequiredInput(pendingTemplate.inputs, inputValues, {
      hideComposerOwnedText: hideFirstOptionalTextOnCard(
        pendingTemplate.inputs,
      ),
    })

  /* C4: a remembered audience that is no longer in the ready list would
     otherwise fail `applyRememberedAudience`'s gate silently — the picker
     just shows its empty placeholder with no explanation. Gated on the fetch
     having actually settled (`audiencesRequested || prelatchAudiences` and
     not still loading), so this never flashes true while the list this same
     card open just requested is still in flight. */
  const rememberedAudienceUnavailable = Boolean(
    pendingTemplate?.inputs.some((spec) => spec.type === 'audience') &&
      rememberedAudience &&
      (audiencesRequested || prelatchAudiences) &&
      !audiencesLoading &&
      !audiences.some((entry) => entry.id === rememberedAudience.audienceId),
  )

  async function startCommand(
    command: ThreadCommand,
    inputs: Record<string, string | null>,
  ) {
    if (isDisabled) {
      return
    }
    /* A native command is not a run. Nothing routes here today (the pick arms
       before it can), and this is the backstop that keeps it that way: report
       mode is an assistant turn, and creating a workflow run for it would
       spend against a template that no longer exists. */
    if (command.kind === 'report') {
      onArmReport?.(command, '')
      return
    }
    /* Same backstop for a skill tag: it is an assistant turn, never a run. */
    if (command.kind === 'skill') {
      onArmSkill?.(command, '')
      return
    }
    if (!threadToken && !ensureThread) {
      return
    }

    const isThreadAction = templateSurface({ slug: command.slug }) === 'thread'
    setStartingSlug(command.slug)
    try {
      const resolvedToken = threadToken ?? (await ensureThread!())
      if (!resolvedToken) {
        return
      }
      const firstTextKey = pendingTemplate
        ? composerOwnedTextInputKey(pendingTemplate.inputs)
        : undefined
      const askTurn = firstTextKey ? (inputs[firstTextKey] ?? '').trim() : ''
      const created = await workflowRunsApi.create({
        templateToken: command.token,
        threadToken: resolvedToken,
        inputs,
        /* Thread-actions keep the historical trigger; lobby plan Starts use
           `user` so schedule attribution stays honest. Same create path. */
        trigger: isThreadAction ? 'thread_action' : 'user',
        ...workflowRunSegmentFields(
          seedMatchesPendingCommand(seed, command) ? seed : null,
        ),
        ...(askTurn ? { askTurn } : {}),
      })
      onRunStarted(created)
      toast.success(command.startedMessage)
    } catch (error) {
      /* Adopts nothing. The 409 for a thread that already has an active run
         lands here too, and adopting on that path would paint this thread with
         a run the click did not start. */
      toast.error(
        getApiErrorMessage(error, t('threads.detail.toast.commandFailed')),
      )
    } finally {
      setStartingSlug(null)
    }
  }

  /* The gate itself: only this, the confirm screen's own button, ever reaches
     `startCommand` or `onArm`. Picking a row from the list only changes
     screen.
     An arming command never reaches `startCommand`: `pendingTemplate`
     is required for `canConfirm` to be true at all, so it is always resolved
     here, and `onArm` gets exactly what `startCommand` would otherwise have
     posted as `inputs`. `/test` arms — Confirm never posts the run.
     Nothing here decides what arming MEANS; that is the mount site's
     job, on the other side of `onArm`. */
  function handleConfirm(command: ThreadCommand) {
    if (!canConfirm) {
      return
    }
    setIsOpen(false)
    setPendingSlug(null)

    if (command.arms) {
      const audienceId = pendingTemplate!.inputs.find(
        (spec) => spec.type === 'audience',
      )
      const pickedId = audienceId ? inputValues[audienceId.key] : null
      if (rememberAudience && pickedId && onRememberAudience) {
        onRememberAudience({
          audienceId: pickedId,
          audienceName:
            audiences.find((entry) => entry.id === pickedId)?.name ?? pickedId,
        })
      } else if (
        /* D6's only forget path: the box opened checked (prefilled === the
           remembered audience) and the reader unticked it before confirming.
           Ticking a DIFFERENT audience never reaches here — `rememberAudience`
           is only true again once the reader re-ticks it by hand, which takes
           the branch above and overwrites instead. */
        !rememberAudience &&
        pickedId &&
        rememberedAudience &&
        pickedId === rememberedAudience.audienceId &&
        onForgetRememberedAudience
      ) {
        onForgetRememberedAudience()
      }
      setAudiencesRequested(true)
      onArm(pendingTemplate!, inputValues, {
        audienceName: audienceNameForInputs(
          pendingTemplate!,
          inputValues,
          audiences,
          rememberedAudience,
        ),
      })
      setRememberAudience(false)
      return
    }

    void startCommand(command, inputValues)
  }

  function openCommand(command: ThreadCommand) {
    seedRestRef.current = null
    appliedAskRestRef.current = null
    setSeedResolveError(null)
    /* Same immediate-arm path a remembered audience takes on `/test`, minus
       the template round trip: a native command declares no inputs, so a
       confirm screen would be an empty card over a button. */
    if (command.kind === 'report') {
      setPendingSlug(null)
      setIsOpen(false)
      onArmReport?.(command, '')
      return
    }
    /* A skill tag declares no inputs either: it arms the composer and closes. */
    if (command.kind === 'skill') {
      setPendingSlug(null)
      setIsOpen(false)
      onArmSkill?.(command, '')
      return
    }
    setInputValues({})
    setRememberAudience(false)
    rememberAudienceTouchedRef.current = false
    setIsFilterStep(false)
    setIsAudiencePickerRevealed(false)
    /* C4: same early request as `openMatched` — start the audience fetch on
       the pick itself, before the template resolves, so the remembered
       prefill (and the checkbox synced to it) does not land a fetch late. */
    setAudiencesRequested(true)
    setPendingSlug(command.slug)
  }

  /* Plan Start may seed a lobby template when the Commands list is empty
     (org with no thread-actions). Keep the confirm shell mountable. A
     by-slug 404 also opens the panel with an error and no pending confirm. */
  const hasCommandList = commands.length > 0
  const hasSeededConfirm = Boolean(pending && isOpen)
  const hasSeedResolveError = Boolean(seedResolveError && isOpen)
  if (
    !isEnabled ||
    (!hasCommandList && !hasSeededConfirm && !hasSeedResolveError && !seed)
  ) {
    return null
  }

  const inlineConfirmation = Boolean(confirmationTarget && pending?.arms)
  const setupTopic = pendingTemplate
    ? inputValues[composerOwnedTextInputKey(pendingTemplate.inputs) ?? '']
    : null
  const panel = isOpen ? (
        <div
          ref={confirmationRef}
          className={cn(
            inlineConfirmation
              ? 'my-3 w-full rounded-2xl border border-primary/20 bg-background p-4 sm:p-5'
              : cn('absolute left-0 z-50 w-[280px] max-w-[calc(100vw-3rem)] rounded-xl p-1',
                  placement === 'below' ? 'top-full mt-2' : 'bottom-full mb-2',
                  composerPanelClassName(appearance)),
          )}
          data-testid="thread-command-menu"
        >
          {pending ? (
            <div className="p-2" data-testid="thread-command-confirm">
              <button
                type="button"
                onClick={() => {
                  if (!showTrigger || (seededExtraCommand && !hasCommandList)) {
                    setIsOpen(false)
                    onDismiss?.()
                    setPendingSlug(null)
                    setSeededExtraCommand(null)
                    setIsFilterStep(false)
                    setIsAudiencePickerRevealed(false)
                    return
                  }
                  setPendingSlug(null)
                  setIsFilterStep(false)
                  setIsAudiencePickerRevealed(false)
                }}
                className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                data-testid="thread-command-back"
              >
                <ChevronLeft className="h-3 w-3" aria-hidden="true" />
                {pending.command}
              </button>

              <div className="flex flex-col gap-3">
                {inlineConfirmation ? <>
                  <Text size="base" weight="medium">{pending.label}</Text>
                  {setupTopic ? <Text size="sm">{setupTopic}</Text> : null}
                </> : null}
                {pending.blurb ? (
                  <Text size="sm" color="muted">
                    {pending.blurb}
                  </Text>
                ) : null}

                {pendingTemplate?.inputs.some(
                  (spec) => spec.key === TWIN_TEST_SEGMENT_BY_INPUT_KEY,
                ) && pendingSeedSegmentLabel ? (
                  <SubsegmentChip
                    label={pendingSeedSegmentLabel}
                    testId="thread-command-segment-label"
                  />
                ) : null}

                {templateError ? (
                  /* The back affordance above stays, so a reader who lands on
                     this can pick something else without shutting the panel. */
                  <Text
                    size="sm"
                    color="destructive"
                    data-testid="thread-command-template-error"
                  >
                    {t('threads.detail.command.templateLoadError')}
                  </Text>
                ) : null}

                {showResolvedAudience && resolvedAudienceOption ? (
                  <div className="flex items-center justify-between gap-2">
                    <Text
                      size="xs"
                      color="muted"
                      data-testid="thread-command-audience-resolved"
                    >
                      {resolvedAudienceOption.rowCount != null
                        ? t('threads.command.audienceResolved', {
                            name: resolvedAudienceOption.name,
                            count: resolvedAudienceOption.rowCount,
                          })
                        : t('threads.command.audienceResolvedNameOnly', {
                            name: resolvedAudienceOption.name,
                          })}
                    </Text>
                    <button
                      type="button"
                      onClick={() => setIsAudiencePickerRevealed(true)}
                      className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                      data-testid="thread-command-audience-change"
                    >
                      {t('threads.command.changeAudience')}
                    </button>
                  </div>
                ) : null}

                {pendingTemplate ? (
                  <ThreadCommandInputs
                    key={pending.slug}
                    inputs={pendingTemplate.inputs}
                    values={inputValues}
                    cutGroups={cutGroups}
                    onFilterStepChange={setIsFilterStep}
                    onChange={(key, value) => {
                      setInputValues((previous) => ({
                        ...previous,
                        [key]: value,
                      }))
                    }}
                    audiences={audiences}
                    audiencesLoading={audiencesLoading}
                    disabled={isDisabled}
                    hideComposerOwnedText={hideFirstOptionalTextOnCard(
                      pendingTemplate.inputs,
                    )}
                    hideAudienceField={showResolvedAudience}
                    /* Arms AND has an audience to remember: `/report` arms
                       too, and a checkbox over a template with no audience
                       input remembers nothing. */
                    showRememberAudience={
                      pending.arms &&
                      pendingTemplate.inputs.some(
                        (spec) => spec.type === 'audience',
                      )
                    }
                    rememberAudience={rememberAudience}
                    onRememberAudienceChange={(remember) => {
                      rememberAudienceTouchedRef.current = true
                      setRememberAudience(remember)
                    }}
                  />
                ) : null}

                {rememberedAudienceUnavailable ? (
                  <Text
                    size="sm"
                    color="muted"
                    data-testid="thread-command-remembered-audience-unavailable"
                  >
                    {t('threads.command.rememberedAudienceUnavailable')}
                  </Text>
                ) : null}

                {isFilterStep ? null : (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      className="mt-1 w-full"
                      data-testid={`thread-command-${commandKey(pending.slug)}-confirm`}
                      disabled={!canConfirm}
                      onClick={() => handleConfirm(pending)}
                    >
                      {pending.confirmLabel}
                    </Button>

                    {isRunActive ? (
                      /* A typed command opens this screen directly, past the
                         trigger whose `title` carries the same reason, so without
                         this line the reader meets a dead button and no cause. */
                      <Text
                        size="sm"
                        color="muted"
                        data-testid="thread-command-blocked"
                      >
                        {disabledReason}
                      </Text>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Scrolls rather than grows: the list is as long as the org's
               seeded catalogue, and the panel is anchored to a control row that
               must stay one line tall. */
            <div data-testid="thread-command-list">
              {seedResolveError ? (
                <Text
                  size="sm"
                  color="destructive"
                  className="px-2.5 py-2"
                  data-testid="thread-command-seed-error"
                >
                  {seedResolveError}
                </Text>
              ) : null}
              <ul
                role="menu"
                aria-label={t('threads.detail.commands')}
                className="max-h-64 overflow-y-auto"
              >
                {commands.map((command) => (
                  <li key={command.slug}>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => openCommand(command)}
                      className="flex w-full items-baseline gap-2 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-muted/70"
                      data-testid={
                        command.kind === 'skill'
                          ? `thread-command-skill-${command.slug}`
                          : `thread-command-option-${commandKey(command.slug)}`
                      }
                    >
                      <span className="font-mono text-xs text-foreground">
                        {command.command}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {command.label}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative flex shrink-0 items-center',
        !showTrigger && 'w-0 self-stretch',
        className,
      )}
      data-testid="thread-commands"
    >
      {showTrigger && hasCommandList ? (
        <button
          type="button"
          onClick={() => {
            setIsOpen((open) => !open)
            setPendingSlug(null)
            setSeededExtraCommand(null)
            setSeedResolveError(null)
            setIsFilterStep(false)
            setIsAudiencePickerRevealed(false)
          }}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={t('threads.detail.commands')}
          disabled={isDisabled}
          title={disabledReason}
          className={composerTriggerClassName(appearance, { isOpen, isSelected })}
          data-testid="thread-command-trigger"
        >
          <SquareSlash className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className={composerTriggerCaptionClassName(appearance)}>
            {t('threads.detail.commands')}
          </span>
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0 opacity-70 transition-transform duration-200',
              composerTriggerChevronClassName(appearance),
              isOpen && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      ) : null}

      {inlineConfirmation && confirmationTarget
        ? createPortal(panel, confirmationTarget)
        : panel}
    </div>
  )
}
